import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  Plus,
  Edit3,
  Trash2,
  Star,
  X,
  ChevronLeft,
  Users,
} from "lucide-react";
import { rateTablesApi, usersApi, rolesApi } from "../../services/api";
import { useNavigate } from "react-router-dom";
import PremiumModal from "../ui/PremiumModal";

interface RateRow {
  label: string;
  hourlyRateCents: number;
  currency: string;
  appliesToRoleIds: string[];
  appliesToUserIds: string[];
}

interface RateTable {
  _id: string;
  name: string;
  isDefault: boolean;
  rows: RateRow[];
  active: boolean;
  createdAt: string;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const RateTablesPage: React.FC = () => {
  const navigate = useNavigate();
  const [rateTables, setRateTables] = useState<RateTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<RateTable | null>(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState("");
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formRows, setFormRows] = useState<RateRow[]>([]);

  const fetchRateTables = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rateTablesApi.list();
      setRateTables(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch rate tables:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRateTables();
  }, [fetchRateTables]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [uRes, rRes] = await Promise.all([
          usersApi.list({ limit: 200 }),
          rolesApi.list(),
        ]);
        setUsers(uRes.data?.data || []);
        setRoles(rRes.data?.data || rRes.data || []);
      } catch (err) {
        console.error("Failed to load users/roles:", err);
      }
    };
    loadData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormIsDefault(false);
    setFormRows([{ label: "", hourlyRateCents: 0, currency: "CAD", appliesToRoleIds: [], appliesToUserIds: [] }]);
    setShowModal(true);
  };

  const openEdit = (rt: RateTable) => {
    setEditing(rt);
    setFormName(rt.name);
    setFormIsDefault(rt.isDefault);
    setFormRows(rt.rows.length > 0 ? rt.rows : [{ label: "", hourlyRateCents: 0, currency: "CAD", appliesToRoleIds: [], appliesToUserIds: [] }]);
    setShowModal(true);
  };

  const addRow = () => {
    setFormRows([...formRows, { label: "", hourlyRateCents: 0, currency: "CAD", appliesToRoleIds: [], appliesToUserIds: [] }]);
  };

  const updateRow = (index: number, field: string, value: any) => {
    setFormRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (index: number) => {
    setFormRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        name: formName,
        isDefault: formIsDefault,
        rows: formRows.filter((r) => r.label),
      };

      if (editing) {
        await rateTablesApi.update(editing._id, data);
      } else {
        await rateTablesApi.create(data);
      }
      setShowModal(false);
      fetchRateTables();
    } catch (err) {
      console.error("Failed to save rate table:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Deactivate this rate table?")) return;
    try {
      await rateTablesApi.remove(id);
      fetchRateTables();
    } catch (err) {
      console.error("Failed to deactivate:", err);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await rateTablesApi.setDefault(id);
      fetchRateTables();
    } catch (err) {
      console.error("Failed to set default:", err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Rate Tables</h1>
            <p className="text-sm text-slate-500 mt-1">
              Define hourly rates for your firm by role and user
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-5 w-5" />
          New Rate Table
        </button>
      </div>

      {/* Rate Tables List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : rateTables.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20 text-slate-500">
          <DollarSign className="h-12 w-12 mb-3 text-slate-300" />
          <p className="font-medium text-lg">No rate tables yet</p>
          <p className="text-sm mt-1">Create your first rate table to define billing rates</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Create Rate Table
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rateTables.map((rt, i) => (
            <motion.div
              key={rt._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-slate-800">{rt.name}</h3>
                  {rt.isDefault && (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <Star className="h-3 w-3" />
                      Default
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!rt.isDefault && (
                    <button
                      onClick={() => handleSetDefault(rt._id)}
                      className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                      title="Set as default"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(rt)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    title="Edit"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeactivate(rt._id)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                    title="Deactivate"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {rt.rows.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-6 py-2 text-xs font-semibold text-slate-600">
                        Role / Label
                      </th>
                      <th className="text-right px-6 py-2 text-xs font-semibold text-slate-600">
                        Hourly Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rt.rows.map((row, j) => (
                      <tr key={j} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-6 py-3 text-slate-800">{row.label}</td>
                        <td className="px-6 py-3 text-right font-semibold text-slate-800">
                          {formatCurrency(row.hourlyRateCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <PremiumModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Rate Table" : "New Rate Table"}
        icon={DollarSign}
        size="xl"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="rateTableForm"
              disabled={saving || !formName}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Create Rate Table"}
            </button>
          </div>
        }
      >
        <form id="rateTableForm" onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Standard Rates"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isDefault"
              checked={formIsDefault}
              onChange={(e) => setFormIsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="isDefault" className="text-sm font-medium text-slate-700">
              Set as default rate table
            </label>
          </div>

          {/* Rate Rows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Rate Rows</label>
              <button
                type="button"
                onClick={addRow}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, transparent)" }}
              >
                + Add Row
              </button>
            </div>

            <div className="space-y-3">
              {formRows.map((row, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-4 relative">
                  {formRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="absolute top-2 right-2 p-1 rounded-lg hover:bg-slate-200 text-slate-400"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Label *</label>
                      <input
                        type="text"
                        required
                        value={row.label}
                        onChange={(e) => updateRow(i, "label", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                        placeholder="e.g. Partner, Associate"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Hourly Rate ($/hr) *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={row.hourlyRateCents ? row.hourlyRateCents / 100 : ""}
                        onChange={(e) =>
                          updateRow(
                            i,
                            "hourlyRateCents",
                            Math.round(parseFloat(e.target.value || "0") * 100)
                          )
                        }
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                        placeholder="350.00"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default RateTablesPage;
